import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { APP_HOME_PATH } from "../lib/app-paths";
import { supabase } from "../lib/supabase";
import { LoginLocationState, MatchDraft } from "../types";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LoginLocationState | null) || {};
  const draft: MatchDraft | undefined = state.draft;
  const isLoginOnly = !draft;

  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCountdown((value) => value - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCountdown]);

  const sendCode = async (event?: FormEvent) => {
    event?.preventDefault();
    let hasError = false;

    if (!email.trim() || !emailPattern.test(email.trim())) {
      setEmailError(
        email.trim() ? t("auth.emailInvalid") : t("auth.emailRequired")
      );
      hasError = true;
    } else {
      setEmailError("");
    }

    if (!name.trim()) {
      setNameError(t("auth.nameRequired"));
      hasError = true;
    } else {
      setNameError("");
    }

    if (hasError) return;

    setLoading(true);
    setMessage("");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          shouldCreateUser: true,
          data: { name: name.trim() },
        },
      });

      if (error) {
        setMessage(t("auth.sendError"));
        return;
      }

      setMessage(t("auth.codeSent"));
      setStep("otp");
      setResendCountdown(30);
    } catch {
      setMessage(t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!otp.trim()) {
      setOtpError(t("auth.codeRequired"));
      return;
    }
    if (otp.trim().length !== 6) {
      setOtpError(t("auth.codeLength"));
      return;
    }

    setOtpError("");
    setLoading(true);
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: otp.trim(),
        type: "email",
      });

      if (error || !session) {
        setMessage(t("auth.codeInvalid"));
        return;
      }

      if (draft) {
        navigate("/crear/jugadores", {
          replace: true,
          state: {
            draft: {
              ...draft,
              userId: session.user.id,
              userEmail: session.user.email,
              userName: name.trim(),
            },
          },
        });
        return;
      }

      navigate(state.from || APP_HOME_PATH, { replace: true });
    } catch {
      setMessage(t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pp-screen">
      <header className="pp-header">
        <Link to={APP_HOME_PATH} className="pp-back">
          {t("common.backHome")}
        </Link>
        <h1>{step === "email" ? t("auth.title") : t("auth.verifyTitle")}</h1>
        <p>
          {step === "email"
            ? isLoginOnly
              ? t("auth.loginSubtitle")
              : t("auth.createSubtitle")
            : t("auth.otpSubtitle")}
        </p>
      </header>

      {draft && step === "email" && (
        <section className="pp-card">
          <p className="pp-muted">{t("auth.creatingMatch")}</p>
          <h2>{draft.location}</h2>
          <p className="pp-muted">
            📅 {draft.date} • ⏰ {draft.time}
          </p>
        </section>
      )}

      {step === "email" ? (
        <form className="pp-card" onSubmit={sendCode}>
          <div className="pp-field">
            <label htmlFor="email">{t("common.email")}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            {emailError && <p className="pp-error">{emailError}</p>}
          </div>
          <div className="pp-field">
            <label htmlFor="name">{t("common.name")}</label>
            <input
              id="name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {nameError && <p className="pp-error">{nameError}</p>}
          </div>
          {message && <p className="pp-muted">{message}</p>}
          <button type="submit" className="pp-btn" disabled={loading}>
            {loading ? t("auth.sendingShort") : t("auth.send")}
          </button>
        </form>
      ) : (
        <form className="pp-card" onSubmit={verifyCode}>
          <div className="pp-field">
            <label htmlFor="otp">{t("auth.codeShort")}</label>
            <input
              id="otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
            />
            {otpError && <p className="pp-error">{otpError}</p>}
          </div>
          {message && <p className="pp-muted">{message}</p>}
          <div className="pp-actions">
            <button type="submit" className="pp-btn" disabled={loading}>
              {loading
                ? t("auth.verifying")
                : isLoginOnly
                ? t("auth.verify")
                : t("auth.verifyAndContinue")}
            </button>
            <button
              type="button"
              className="pp-btn-secondary"
              disabled={loading || resendCountdown > 0}
              onClick={() => sendCode()}
            >
              {resendCountdown > 0
                ? t("auth.resendIn", { count: resendCountdown })
                : t("auth.resendCode")}
            </button>
          </div>
        </form>
      )}
    </main>
  );
};
